// app/src/dev/ContributeV2VerifyPanel.tsx
import React, { useMemo, useState } from "react";
import { runContributeV2Verify } from "../simulations/runContributeV2Verify";

type Props = {
  defaultPoolV2?: string;
  defaultWallet?: string;
  defaultMemberV2?: string;
  defaultVaultV2?: string;
};

function shortPk(s?: string) {
  if (!s) return "—";
  return s.length > 12 ? `${s.slice(0, 4)}…${s.slice(-4)}` : s;
}

export default function ContributeV2VerifyPanel(props: Props) {
  const [poolV2, setPoolV2] = useState(props.defaultPoolV2 || "");
  const [wallet, setWallet] = useState(props.defaultWallet || "");
  const [memberV2, setMemberV2] = useState(props.defaultMemberV2 || "");
  const [vaultV2, setVaultV2] = useState(props.defaultVaultV2 || "");

  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [result, setResult] = useState<any>(null);

  const phantomPk = (window as any)?.solana?.publicKey?.toString?.() ?? "";

  const canRun = useMemo(() => {
    return poolV2.trim().length > 20;
  }, [poolV2]);

  async function onVerify() {
    setBusy(true);
    setStatus("");
    setResult(null);

    try {
      const res = await runContributeV2Verify({
        pool_v2: poolV2,
        wallet: wallet.trim() || phantomPk,
        member_v2: memberV2.trim() || undefined,
        vault_v2: vaultV2.trim() || undefined,
        commitment: "confirmed",
      });

      setResult(res);

      // Executive summary line (operator-friendly)
      const totalContrib = res?.decoded?.member?.data?.total_contributed ?? "—";
      const vaultLamports = res?.vault?.lamports ?? "—";

      setStatus(
        `VERIFY OK\n` +
          `member.total_contributed: ${String(totalContrib)}\n` +
          `vault.lamports: ${String(vaultLamports)}\n` +
          `member/vault derived match: ${
            res?.addresses?.member_v2 === res?.addresses?.derived?.member_v2 &&
            res?.addresses?.vault_v2 === res?.addresses?.derived?.vault_v2
              ? "YES"
              : "NO (check overrides)"
          }`
      );

      console.log("[ContributeV2VerifyPanel] Verify result:", res);
    } catch (e: any) {
      const msg = e?.message ? String(e.message) : String(e);
      setStatus(`VERIFY FAILED\n${msg}`);
      console.error(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ border: "1px solid #333", borderRadius: 12, padding: 16, margin: "16px 0" }}>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Contribute V2 — Verify On-Chain State</div>

      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 12 }}>
        Phantom wallet: <b>{phantomPk ? shortPk(phantomPk) : "not connected"}</b>
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <label>
          <div style={{ fontSize: 12, opacity: 0.8 }}>pool_v2 (required)</div>
          <input
            value={poolV2}
            onChange={(e) => setPoolV2(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 8 }}
          />
        </label>

        <label>
          <div style={{ fontSize: 12, opacity: 0.8 }}>wallet (optional; defaults to Phantom)</div>
          <input
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 8 }}
          />
        </label>

        <label>
          <div style={{ fontSize: 12, opacity: 0.8 }}>member_v2 (optional override; normally derived)</div>
          <input
            value={memberV2}
            onChange={(e) => setMemberV2(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 8 }}
          />
        </label>

        <label>
          <div style={{ fontSize: 12, opacity: 0.8 }}>vault_v2 (optional override; normally derived)</div>
          <input
            value={vaultV2}
            onChange={(e) => setVaultV2(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 8 }}
          />
        </label>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={onVerify}
            disabled={!canRun || busy}
            style={{ padding: "10px 14px", borderRadius: 10, cursor: "pointer" }}
          >
            {busy ? "Verifying..." : "Verify"}
          </button>
        </div>

        {status ? (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "#111", whiteSpace: "pre-wrap" }}>
            {status}
          </div>
        ) : null}

        {result ? (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "#0b111a" }}>
            <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 8 }}>
              Full result logged to Console. Snapshot below:
            </div>
            <pre style={{ margin: 0, fontSize: 12, overflow: "auto" }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        ) : null}
      </div>
    </div>
  );
}
